import { useEffect, useState } from "react";
import { X, Save } from "lucide-react";
import { ig } from "../inbox/_inbox_shared";
import { api } from "../../../lib/apiClient";

export default function AddContactModal({ onClose, onCreated, meta, defaultWorkspace, defaultAgency }) {
  const [form, setForm] = useState({
    name: "", first_name: "", last_name: "",
    email: "", phone: "", business: "",
    workspace_id: defaultWorkspace || "",
    owner_user_id: "",
    agency_id: defaultAgency || "",
    status: "active",
    source: "manual",
    tags: "",
  });
  const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form.workspace_id && meta?.workspaces?.length) {
      setForm((f) => ({ ...f, workspace_id: meta.workspaces[0].id }));
    }
  }, [meta, form.workspace_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.workspace_id) { setErr("Workspace is required"); return; }
    if (!form.name && !form.first_name && !form.email && !form.phone) {
      setErr("Provide at least name, email, or phone"); return;
    }
    setBusy(true); setErr(null);
    try {
      await api.post("/api/admin/contacts", {
        ...form,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
        owner_user_id: form.owner_user_id || undefined,
        agency_id: form.agency_id || undefined,
      });
      onCreated();
    } catch (e) { setErr(e.message || "Create failed"); } finally { setBusy(false); }
  };

  return (
    <div data-testid="add-contact-modal" onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={panel}>
        <div style={header}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Add contact</h2>
          <button onClick={onClose} style={iconBtn} data-testid="add-contact-close"><X size={13} /></button>
        </div>
        <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, overflowY: "auto" }}>
          <Fld label="First name"><input style={input} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="ac-first" /></Fld>
          <Fld label="Last name"><input style={input}  value={form.last_name}  onChange={(e) => set("last_name", e.target.value)} data-testid="ac-last" /></Fld>
          <Fld label="Full name"><input style={input}  value={form.name}       onChange={(e) => set("name", e.target.value)} placeholder="Optional if first + last given" data-testid="ac-name" /></Fld>
          <Fld label="Business"><input style={input}   value={form.business}   onChange={(e) => set("business", e.target.value)} data-testid="ac-business" /></Fld>
          <Fld label="Email"><input style={input}      value={form.email}      onChange={(e) => set("email", e.target.value)} type="email" data-testid="ac-email" /></Fld>
          <Fld label="Phone"><input style={input}      value={form.phone}      onChange={(e) => set("phone", e.target.value)} data-testid="ac-phone" /></Fld>
          <Fld label="Workspace *">
            <select style={input} value={form.workspace_id} onChange={(e) => set("workspace_id", e.target.value)} data-testid="ac-workspace">
              <option value="">Select workspace</option>
              {meta?.workspaces?.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Fld>
          <Fld label="Owner">
            <select style={input} value={form.owner_user_id} onChange={(e) => set("owner_user_id", e.target.value)} data-testid="ac-owner">
              <option value="">Default (current admin)</option>
              {meta?.owners?.map((o) => <option key={o.id} value={o.id}>{o.name || o.email}</option>)}
            </select>
          </Fld>
          <Fld label="Status">
            <select style={input} value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="ac-status">
              {(meta?.statuses || ["active", "inactive", "unsubscribed", "bounced"]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Fld>
          <Fld label="Source">
            <select style={input} value={form.source} onChange={(e) => set("source", e.target.value)} data-testid="ac-source">
              {(meta?.sources || ["manual", "form", "import", "api", "chat", "call", "website", "facebook_ad", "google_ad"]).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Fld>
          <Fld label="Tags (comma separated)" span2><input style={input} value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="hot_lead, warm, business_funding" data-testid="ac-tags" /></Fld>
          {err && <div style={{ color: "#B91C1C", fontSize: 12, gridColumn: "1 / -1" }}>{err}</div>}
        </div>
        <div style={footer}>
          <div style={{ flex: 1 }} />
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={submit} disabled={busy} data-testid="ac-submit">
            <Save size={13} /> {busy ? "Saving…" : "Create contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Fld({ label, children, span2 }) {
  return (
    <div style={{ gridColumn: span2 ? "1 / -1" : "auto" }}>
      <div style={{ fontSize: 11.5, color: ig.ink, fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}
const backdrop = { position: "fixed", inset: 0, background: "rgba(7,31,24,0.42)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const panel    = { background: ig.card, width: 640, maxWidth: "94vw", maxHeight: "90vh", borderRadius: 14, boxShadow: ig.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" };
const header   = { padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${ig.border}` };
const footer   = { padding: "12px 20px", borderTop: `1px solid ${ig.border}`, display: "flex", alignItems: "center", gap: 8 };
const iconBtn  = { width: 34, height: 34, borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, cursor: "pointer", display: "inline-grid", placeItems: "center" };
const input    = { width: "100%", height: 36, padding: "0 11px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink };
const btnPrimary   = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: 0, background: "linear-gradient(135deg, #18B86B, #22C55E)", color: "#fff", fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
const btnSecondary = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink, fontWeight: 700, fontSize: 12.5, cursor: "pointer" };
